import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTimes,
  FaTasks,
  FaCheckCircle,
  FaSpinner,
  FaChevronDown,
  FaChevronRight,
  FaRegCircle,
  FaComments,
  FaPaperclip,
  FaDownload,
  FaClipboardList,
  FaBriefcase,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { PaymentMilestone, OfferLineItem, OfferLineScopeItem } from '../services/OfferService';
import PlantafelService, { JobDetail, ChecklistItem, JobComment, JobAttachment } from '../../operations/services/PlantafelService';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface LineItemWithScope extends OfferLineItem {
  scopeItems: OfferLineScopeItem[];
  scopeLoading: boolean;
}

interface JobWithChecklist extends JobDetail {
  checklist: ChecklistItem[];
  checklistLoading: boolean;
}

interface MilestoneTasksModalProps {
  isOpen: boolean;
  milestone: PaymentMilestone | null;
  lineItems?: OfferLineItem[];
  onClose: () => void;
  onTasksChanged?: () => void;
}

interface ExpandedSubtask {
  jobId: number;
  itemId: number;
}

type ViewMode = 'scope' | 'jobs';

export default function MilestoneTasksModal({
  isOpen,
  milestone,
  lineItems,
  onClose,
}: MilestoneTasksModalProps): React.JSX.Element | null {
  // Scope Items state (from Offer)
  const [scopeItems, setScopeItems] = useState<LineItemWithScope[]>([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [expandedLineItems, setExpandedLineItems] = useState<Set<number>>(new Set());

  // Jobs state (actual work)
  const [jobs, setJobs] = useState<JobWithChecklist[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<Set<number>>(new Set());
  const [expandedSubtask, setExpandedSubtask] = useState<ExpandedSubtask | null>(null);
  const [subtaskComments, setSubtaskComments] = useState<JobComment[]>([]);
  const [subtaskAttachments, setSubtaskAttachments] = useState<JobAttachment[]>([]);
  const [subtaskLoading, setSubtaskLoading] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('jobs');

  // Load Scope Items from Offer
  const loadScopeItems = useCallback(async () => {
    if (!milestone?.id || !lineItems) return;

    setScopeLoading(true);
    try {
      console.log('[MilestoneTasksModal] Loading scope for milestone:', milestone.id, milestone.label);
      console.log('[MilestoneTasksModal] All lineItems:', lineItems.map(i => ({ id: i.id, name: i.name, payment_milestone_id: i.payment_milestone_id })));

      const milestoneItems = lineItems.filter(
        item => item.payment_milestone_id === milestone.id
      );
      console.log('[MilestoneTasksModal] Filtered milestoneItems:', milestoneItems.map(i => ({ id: i.id, name: i.name })));

      const itemsWithScope: LineItemWithScope[] = milestoneItems.map(item => ({
        ...item,
        scopeItems: [],
        scopeLoading: true,
      }));

      setScopeItems(itemsWithScope);
      setExpandedLineItems(new Set(milestoneItems.filter(i => i.id).map(i => i.id as number)));

      // Load scope for all items
      for (const item of milestoneItems) {
        if (!item.id) continue;
        try {
          const response = await OfferService.fetchOfferLineScopeItems(item.id);
          console.log(`[MilestoneTasksModal] Scope items for line ${item.id} (${item.name}):`, response.scope_items);
          setScopeItems(prev => prev.map(i =>
            i.id === item.id ? { ...i, scopeItems: response.scope_items, scopeLoading: false } : i
          ));
        } catch (err) {
          console.error('Failed to load scope:', err);
          setScopeItems(prev => prev.map(i =>
            i.id === item.id ? { ...i, scopeLoading: false } : i
          ));
        }
      }
    } catch (err) {
      console.error('Failed to load scope items:', err);
    } finally {
      setScopeLoading(false);
    }
  }, [milestone?.id, lineItems]);

  // Load Jobs with Checklists
  const loadJobs = useCallback(async () => {
    if (!milestone?.id) return;

    setJobsLoading(true);
    try {
      const jobsData = await PlantafelService.fetchJobsByMilestone(milestone.id);

      const jobsWithChecklist: JobWithChecklist[] = jobsData.map(job => ({
        ...job,
        checklist: [],
        checklistLoading: true,
      }));

      setJobs(jobsWithChecklist);
      setExpandedJobs(new Set(jobsData.map(j => j.id)));

      // Load checklists in parallel
      const checklistPromises = jobsData.map(async (job) => {
        try {
          const checklist = await PlantafelService.fetchJobChecklist(job.id);
          return { jobId: job.id, checklist };
        } catch (err) {
          return { jobId: job.id, checklist: [] };
        }
      });

      const checklistResults = await Promise.all(checklistPromises);

      setJobs(prev => prev.map(job => {
        const result = checklistResults.find(r => r.jobId === job.id);
        return {
          ...job,
          checklist: result?.checklist || [],
          checklistLoading: false,
        };
      }));
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setJobsLoading(false);
    }
  }, [milestone?.id]);

  useEffect(() => {
    if (isOpen && milestone?.id) {
      loadScopeItems();
      loadJobs();
      setExpandedSubtask(null);
      setSubtaskComments([]);
      setSubtaskAttachments([]);
    }
  }, [isOpen, milestone?.id, loadScopeItems, loadJobs]);

  // Auto-switch to scope view if no jobs
  useEffect(() => {
    if (!jobsLoading && jobs.length === 0 && scopeItems.length > 0) {
      setViewMode('scope');
    } else if (!jobsLoading && jobs.length > 0) {
      setViewMode('jobs');
    }
  }, [jobsLoading, jobs.length, scopeItems.length]);

  const toggleLineItemExpand = (itemId: number | undefined) => {
    if (!itemId) return;
    setExpandedLineItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleJobExpand = (jobId: number) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const handleSubtaskClick = async (jobId: number, item: ChecklistItem) => {
    if (expandedSubtask?.jobId === jobId && expandedSubtask?.itemId === item.id) {
      setExpandedSubtask(null);
      setSubtaskComments([]);
      setSubtaskAttachments([]);
      return;
    }

    setExpandedSubtask({ jobId, itemId: item.id });
    setSubtaskLoading(true);
    setSubtaskComments([]);
    setSubtaskAttachments([]);

    try {
      const [comments, attachments] = await Promise.all([
        PlantafelService.fetchJobComments(jobId, item.id),
        PlantafelService.fetchJobAttachments(jobId, item.id),
      ]);
      setSubtaskComments(comments);
      setSubtaskAttachments(attachments);
    } catch (err) {
      console.error('Failed to load subtask data:', err);
    } finally {
      setSubtaskLoading(false);
    }
  };

  if (!isOpen || !milestone) return null;

  const totalJobs = jobs.length;
  const doneJobs = jobs.filter(j => j.status === 'DONE').length;
  const totalChecklist = jobs.reduce((sum, job) => sum + (job.checklist?.length || 0), 0);
  const doneChecklist = jobs.reduce((sum, job) =>
    sum + (job.checklist?.filter(c => c.is_done).length || 0), 0);
  const totalScopeItems = scopeItems.reduce((sum, item) => sum + (item.scope_items_count || 0), 0);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FaTasks size={18} color={colors.textMuted} />
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.navy }}>
                  {milestone.label}
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.textMuted }}>
                  {totalJobs > 0
                    ? `${totalJobs} Jobs (${doneJobs} erledigt) • ${totalChecklist} Subtasks (${doneChecklist} erledigt)`
                    : `${scopeItems.length} Leistungen • ${totalScopeItems} Subtasks geplant`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: colors.textMuted }}
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* View Toggle - only show if we have both */}
          {jobs.length > 0 && scopeItems.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => setViewMode('jobs')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 500,
                  border: viewMode === 'jobs' ? `2px solid ${colors.navy}` : `1px solid ${colors.border}`,
                  background: viewMode === 'jobs' ? colors.infoLight : colors.bgCard,
                  color: viewMode === 'jobs' ? colors.navy : colors.textMuted,
                  cursor: 'pointer',
                }}
              >
                <FaBriefcase size={12} />
                Jobs & Dokumentation ({totalJobs})
              </button>
              <button
                onClick={() => setViewMode('scope')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 500,
                  border: viewMode === 'scope' ? `2px solid ${colors.navy}` : `1px solid ${colors.border}`,
                  background: viewMode === 'scope' ? colors.infoLight : colors.bgCard,
                  color: viewMode === 'scope' ? colors.navy : colors.textMuted,
                  cursor: 'pointer',
                }}
              >
                <FaClipboardList size={12} />
                Leistungsumfang ({scopeItems.length})
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          {viewMode === 'jobs' ? (
            // Jobs View
            jobsLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>
                <FaSpinner size={24} className="spin" />
                <p style={{ marginTop: '12px' }}>Jobs werden geladen...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: colors.textLight }}>
                <FaBriefcase size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                <p style={{ margin: 0 }}>Keine Jobs für diesen Milestone</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>
                  {scopeItems.length > 0
                    ? 'Wechsle zu "Leistungsumfang" um die geplanten Subtasks zu sehen.'
                    : 'Jobs werden erstellt wenn das Angebot angenommen wird.'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {jobs.map((job) => {
                  const isJobExpanded = expandedJobs.has(job.id);
                  const checklist = job.checklist || [];
                  const doneCount = checklist.filter(c => c.is_done).length;
                  const isDone = job.status === 'DONE';

                  return (
                    <div
                      key={job.id}
                      style={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: isDone ? colors.bgMuted : colors.bgCard,
                      }}
                    >
                      {/* Job Header */}
                      <div
                        onClick={() => toggleJobExpand(job.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '14px 16px', cursor: 'pointer',
                          background: isDone ? colors.successLight : colors.bgCard,
                        }}
                      >
                        <span style={{ color: colors.textMuted }}>
                          {isJobExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                        </span>
                        {isDone ? (
                          <FaCheckCircle size={16} style={{ color: colors.success, flexShrink: 0 }} />
                        ) : (
                          <FaRegCircle size={16} style={{ color: colors.border, flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: isDone ? colors.success : colors.navy }}>
                            {job.title}
                          </div>
                          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
                            {checklist.length > 0 ? `${doneCount}/${checklist.length} Subtasks erledigt` : 'Keine Subtasks'}
                            {job.status && ` • Status: ${job.status}`}
                          </div>
                        </div>
                      </div>

                      {/* Checklist Items */}
                      {isJobExpanded && checklist.length > 0 && (
                        <div style={{ borderTop: `1px solid ${colors.border}`, background: colors.bgMuted }}>
                          <div style={{ padding: '8px 0' }}>
                            {checklist.map((item) => {
                              const isSubtaskExpanded = expandedSubtask?.jobId === job.id && expandedSubtask?.itemId === item.id;
                              const hasContent = (item.comments_count || 0) > 0 || (item.attachments_count || 0) > 0;

                              return (
                                <div key={item.id}>
                                  <div
                                    onClick={() => handleSubtaskClick(job.id, item)}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '10px',
                                      padding: '10px 16px', cursor: 'pointer',
                                      background: isSubtaskExpanded ? colors.infoLight : 'transparent',
                                      borderRadius: '4px', margin: '0 8px',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={(e) => { if (!isSubtaskExpanded) e.currentTarget.style.background = colors.bgMuted; }}
                                    onMouseLeave={(e) => { if (!isSubtaskExpanded) e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    <span style={{ color: isSubtaskExpanded ? colors.navy : colors.textLight, width: '14px' }}>
                                      {isSubtaskExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                                    </span>
                                    {item.is_done ? (
                                      <FaCheckCircle size={14} style={{ color: colors.success, flexShrink: 0 }} />
                                    ) : (
                                      <FaRegCircle size={14} style={{ color: colors.border, flexShrink: 0 }} />
                                    )}
                                    <span style={{
                                      fontSize: '13px', flex: 1,
                                      color: item.is_done ? colors.success : colors.textBody,
                                      textDecoration: item.is_done ? 'line-through' : 'none',
                                    }}>
                                      {item.text || item.name || item.title}
                                    </span>
                                    {hasContent && (
                                      <div style={{ display: 'flex', gap: '6px' }}>
                                        {(item.comments_count || 0) > 0 && (
                                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: colors.textMuted, background: colors.border, padding: '2px 6px', borderRadius: '10px' }}>
                                            <FaComments size={9} /> {item.comments_count}
                                          </span>
                                        )}
                                        {(item.attachments_count || 0) > 0 && (
                                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: colors.textMuted, background: colors.border, padding: '2px 6px', borderRadius: '10px' }}>
                                            <FaPaperclip size={9} /> {item.attachments_count}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Expanded Subtask Content */}
                                  {isSubtaskExpanded && (
                                    <div style={{ margin: '0 8px 8px 8px', padding: '12px', background: colors.bgCard, borderRadius: '6px', border: `1px solid ${colors.border}` }}>
                                      {subtaskLoading ? (
                                        <div style={{ textAlign: 'center', padding: '12px', color: colors.textLight, fontSize: '12px' }}>
                                          <FaSpinner size={14} className="spin" /> Laden...
                                        </div>
                                      ) : subtaskComments.length > 0 || subtaskAttachments.length > 0 ? (
                                        <>
                                          {subtaskComments.map((comment) => {
                                            const commentPhotos = subtaskAttachments.filter(a => a.comment_id === comment.id && a.content_type?.startsWith('image/'));
                                            const commentFiles = subtaskAttachments.filter(a => a.comment_id === comment.id && !a.content_type?.startsWith('image/'));
                                            return (
                                              <div key={comment.id} style={{ background: colors.bgMuted, padding: '10px 12px', borderRadius: '6px', fontSize: '12px', marginBottom: '8px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                  <span style={{ fontWeight: 600, color: colors.textBody }}>{comment.author_name}</span>
                                                  <span style={{ color: colors.textLight, fontSize: '11px' }}>
                                                    {new Date(comment.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                                  </span>
                                                </div>
                                                {comment.text !== '(Dateianhang)' && (
                                                  <p style={{ margin: 0, color: colors.textBody, whiteSpace: 'pre-wrap' }}>{comment.text}</p>
                                                )}
                                                {commentPhotos.length > 0 && (
                                                  <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                                                    {commentPhotos.map(photo => (
                                                      <a key={photo.id} href={photo.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', paddingBottom: '100%', position: 'relative', background: colors.bgMuted, borderRadius: '4px', overflow: 'hidden' }}>
                                                        <img src={photo.thumbnail_url || photo.file_url} alt={photo.file_name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                                      </a>
                                                    ))}
                                                  </div>
                                                )}
                                                {commentFiles.length > 0 && (
                                                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    {commentFiles.map(file => (
                                                      <a key={file.id} href={file.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: colors.bgCard, borderRadius: '4px', fontSize: '11px', color: colors.navy, textDecoration: 'none' }}>
                                                        <FaPaperclip size={10} />
                                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.file_name}</span>
                                                        <FaDownload size={10} style={{ color: colors.textMuted }} />
                                                      </a>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                          {/* Standalone Attachments */}
                                          {(() => {
                                            const standalonePhotos = subtaskAttachments.filter(a => !a.comment_id && a.content_type?.startsWith('image/'));
                                            const standaloneFiles = subtaskAttachments.filter(a => !a.comment_id && !a.content_type?.startsWith('image/'));
                                            if (standalonePhotos.length === 0 && standaloneFiles.length === 0) return null;
                                            return (
                                              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${colors.border}` }}>
                                                <div style={{ fontSize: '11px', fontWeight: 600, color: colors.textMuted, marginBottom: '6px' }}>Dateien</div>
                                                {standalonePhotos.length > 0 && (
                                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '6px' }}>
                                                    {standalonePhotos.map(photo => (
                                                      <a key={photo.id} href={photo.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', paddingBottom: '100%', position: 'relative', background: colors.bgMuted, borderRadius: '4px', overflow: 'hidden' }}>
                                                        <img src={photo.thumbnail_url || photo.file_url} alt={photo.file_name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                                      </a>
                                                    ))}
                                                  </div>
                                                )}
                                                {standaloneFiles.map(file => (
                                                  <a key={file.id} href={file.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: colors.bgMuted, borderRadius: '4px', fontSize: '11px', color: colors.navy, textDecoration: 'none', marginBottom: '4px' }}>
                                                    <FaPaperclip size={10} />
                                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.file_name}</span>
                                                    <FaDownload size={10} style={{ color: colors.textMuted }} />
                                                  </a>
                                                ))}
                                              </div>
                                            );
                                          })()}
                                        </>
                                      ) : (
                                        <div style={{ textAlign: 'center', padding: '12px', color: colors.textLight, fontSize: '12px' }}>
                                          Keine Kommentare oder Anhänge
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // Scope Items View (from Offer)
            scopeLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>
                <FaSpinner size={24} className="spin" />
                <p style={{ marginTop: '12px' }}>Leistungen werden geladen...</p>
              </div>
            ) : scopeItems.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: colors.textLight }}>
                <FaClipboardList size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                <p style={{ margin: 0 }}>Keine Leistungen für diesen Milestone</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {scopeItems.map((item) => {
                  const isExpanded = item.id ? expandedLineItems.has(item.id) : false;
                  const scopeCount = item.scope_items_count || 0;

                  return (
                    <div
                      key={item.id || item.position}
                      style={{ border: `1px solid ${colors.border}`, borderRadius: '8px', overflow: 'hidden' }}
                    >
                      <div
                        onClick={() => toggleLineItemExpand(item.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '14px 16px', background: colors.bgCard,
                          cursor: scopeCount > 0 ? 'pointer' : 'default',
                        }}
                      >
                        {scopeCount > 0 && (
                          <span style={{ color: colors.textMuted }}>
                            {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                          </span>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>{item.name}</div>
                          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
                            {item.quantity} {item.unit} × {OfferService.formatCurrency(item.unit_price)}
                            {scopeCount > 0 && ` • ${scopeCount} Subtasks`}
                          </div>
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>
                          {OfferService.formatCurrency(item.total_net)}
                        </span>
                      </div>

                      {isExpanded && scopeCount > 0 && (
                        <div style={{ borderTop: `1px solid ${colors.border}`, background: colors.bgMuted }}>
                          {item.scopeLoading ? (
                            <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted }}>
                              <FaSpinner size={14} className="spin" />
                            </div>
                          ) : item.scopeItems.length === 0 ? (
                            <div style={{ padding: '16px', textAlign: 'center', color: colors.textLight, fontSize: '13px' }}>
                              Keine Subtasks vorhanden
                            </div>
                          ) : (
                            <div style={{ padding: '8px 0' }}>
                              {item.scopeItems.map((scope, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px' }}>
                                  <FaRegCircle size={12} style={{ color: colors.border, flexShrink: 0 }} />
                                  <span style={{ fontSize: '13px', color: colors.textBody }}>{scope.text}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              ...ps.buttonSecondary,
            }}
          >
            Schließen
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
